import React from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';

const Footer = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const t = translations[language];
  const isROPage = location.pathname === '/ro';
  
  return (
    <footer className={`relative ${isROPage ? 'bg-blue-900' : 'bg-black'} text-white py-16 overflow-hidden border-t-2 ${isROPage ? 'border-blue-400/30' : 'border-green-500/30'}`}>
      {/* Background Pattern */}
      {!isROPage && <div className="absolute inset-0 security-grid opacity-10"></div>}
      
      {/* Scan Line Effect */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute w-full h-0.5 bg-gradient-to-r from-transparent via-green-500/20 to-transparent animate-scan-line"></div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          <div>
            <h3 className={`text-3xl font-extrabold mb-6 bg-gradient-to-r ${isROPage ? 'from-blue-400 to-cyan-400' : 'from-green-400 to-cyan-400'} bg-clip-text text-transparent font-mono`}>
              SMART SECURITY AND RO SYSTEMS
            </h3>
            <p className="text-gray-400 leading-relaxed mb-6">
              {isROPage 
                ? 'Smart Security And RO Systems - Your trusted partner for water purification and security solutions in Raptinagar, Gorakhpur.'
                : 'Smart Security And RO Systems - Your trusted partner for advanced security and surveillance solutions in Raptinagar, Gorakhpur.'
              }
            </p>
            <div className="flex space-x-4">
              {[
                { icon: '📞', link: 'tel:+919795200337' },
                { icon: '📧', link: 'mailto:ashishtripathi348@gmail.com' },
                { icon: '🌐', link: 'https://www.welcome-ro-security-system.com' }
              ].map((social, index) => (
                <a
                  key={index}
                  href={social.link}
                  className={`w-12 h-12 ${isROPage ? 'bg-blue-800' : 'bg-gray-800'} border-2 ${isROPage ? 'border-blue-400/30 hover:bg-blue-500/20 hover:border-blue-400/60' : 'border-green-500/30 hover:bg-green-500/20 hover:border-green-500/60'} rounded-lg flex items-center justify-center text-xl transition-all duration-300 transform hover:scale-110`}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-xl font-bold mb-6 flex items-center font-mono">
              <span className={`w-1 h-6 bg-gradient-to-b ${isROPage ? 'from-blue-400 to-cyan-400' : 'from-green-400 to-cyan-400'} rounded-full mr-3`}></span>
              {t.footerQuickLinks}
            </h4>
            <ul className="space-y-3">
              {[
                { name: t.footerAbout, href: '#about' },
                { name: t.footerServices, href: '#services' },
                { name: t.footerContact, href: '#contact' }
              ].map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href} 
                    className={`text-gray-400 hover:${isROPage ? 'text-blue-400' : 'text-green-400'} transition-colors flex items-center group font-mono`}
                  >
                    <div className={`w-2 h-2 ${isROPage ? 'bg-blue-500' : 'bg-green-500'} rounded-full mr-2 opacity-0 group-hover:opacity-100 transition-opacity`}></div>
                    {language === 'en' ? link.name.toUpperCase() : link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xl font-bold mb-6 flex items-center font-mono">
              <span className={`w-1 h-6 bg-gradient-to-b ${isROPage ? 'from-cyan-400 to-blue-400' : 'from-cyan-400 to-green-400'} rounded-full mr-3`}></span>
              {t.footerContactInfo}
            </h4>
            <ul className="space-y-4">
              {[
                {
                  icon: (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  ),
                  text: '+91 9795200337',
                  link: 'tel:+919795200337'
                },
                {
                  icon: (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  ),
                  text: 'ashishtripathi348@gmail.com',
                  link: 'mailto:ashishtripathi348@gmail.com'
                },
                {
                  icon: (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  ),
                  text: 'Raptinagar, Gorakhpur',
                  link: null
                }
              ].map((item, index) => (
                <li key={index} className="flex items-start group">
                  <div className={`${isROPage ? 'text-blue-400 group-hover:text-blue-300' : 'text-green-400 group-hover:text-green-300'} mr-3 mt-0.5 transition-colors`}>
                    {item.icon}
                  </div>
                  {item.link ? (
                    <a href={item.link} className={`text-gray-400 hover:${isROPage ? 'text-blue-400' : 'text-green-400'} transition-colors break-all`}>
                      {item.text}
                    </a>
                  ) : (
                    <span className="text-gray-400">{item.text}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className={`border-t ${isROPage ? 'border-blue-400/30' : 'border-green-500/30'} pt-8`}>
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 mb-4 md:mb-0 font-mono text-sm">
              &copy; {new Date().getFullYear()} Smart Security And RO Systems. {language === 'en' ? 'All rights reserved.' : 'सभी अधिकार सुरक्षित।'}
            </p>
            <p className="text-gray-500 flex items-center font-mono text-sm">
              <span className={`${isROPage ? 'text-blue-400' : 'text-green-500'} mr-2`}>{t.footerActive}</span>
              {t.footerSolutions}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

